import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import axios, { type AxiosError } from "axios";
import { getURLPrefix } from "utils/getUrlPrefix";
import type { setErrors } from "./errorReducers";
import { type URLSearchParams } from "url";

const URL_PREFIX = getURLPrefix();

const initialState = {
  amazon_advertising_connecting: false,
  amazon_advertising_access: "",
  amazon_additional_information: "" as string | boolean,
  amazon_mws_access: "",
  eu_amazon_mws_access: "",
  fe_amazon_mws_access: "",
  about_your_company: "",
  payment_discovery: "",
  org_status: "",
  listings_progress: "",
  category_progress: "",
  stock_progress: "",
  remainder_days: "",
  visited_category_management: false,
  loaded: false,
  amazon_spapi_access: "",
  eu_amazon_spapi_access: "",
  fe_amazon_spapi_access: "",
};

const amzOnboardSlice = createSlice({
  name: "amz_onboard",
  initialState,
  reducers: {
    initiateAmzAdvConnect: (state) => {
      state.amazon_advertising_connecting = true;
      state.loaded = true;
    },
    completeAmzAdvConnect: (state) => {
      state.amazon_advertising_connecting = false;
      state.loaded = true;
    },
    completeAmzProfileSelection: (state) => {
      state.amazon_additional_information = true;
      state.amazon_advertising_connecting = false;
      state.loaded = true;
    },
    updateAmzOnboardingStatus: (state, action: PayloadAction<typeof initialState>) => {
      state.amazon_advertising_access = action.payload.amazon_advertising_access;
      state.amazon_additional_information = action.payload.amazon_additional_information;
      state.amazon_mws_access = action.payload.amazon_mws_access;
      state.eu_amazon_mws_access = action.payload.eu_amazon_mws_access;
      state.fe_amazon_mws_access = action.payload.fe_amazon_mws_access;
      state.about_your_company = action.payload.about_your_company;
      state.payment_discovery = action.payload.payment_discovery;
      state.org_status = action.payload.org_status;
      state.listings_progress = action.payload.listings_progress;
      state.category_progress = action.payload.category_progress;
      state.stock_progress = action.payload.stock_progress;
      state.remainder_days = action.payload.remainder_days;
      state.visited_category_management = action.payload.visited_category_management;
      state.loaded = true;
      state.amazon_spapi_access = action.payload.amazon_spapi_access;
      state.eu_amazon_spapi_access = action.payload.eu_amazon_spapi_access;
      state.fe_amazon_spapi_access = action.payload.fe_amazon_spapi_access;
    },
    resetAmzOnboarding: () => {
      return initialState;
    },
  },
});

export const {
  initiateAmzAdvConnect,
  completeAmzAdvConnect,
  completeAmzProfileSelection,
  updateAmzOnboardingStatus,
  resetAmzOnboarding,
} = amzOnboardSlice.actions;
export default amzOnboardSlice.reducer;

export const refreshAmzOnboardStatus = (
  dispatchUpdateAmzOnboardingStatus: (status: typeof initialState) => void,
  accessToken: string,
  dispatchSetErrors: typeof setErrors,
  amzadv_code: string | null = null,
  code_callback?: () => void,
  searchParams?: URLSearchParams,
) => {
  const AuthHeader = "Bearer ".concat(accessToken);
  function refreshStatus() {
    axios
      .get(URL_PREFIX + "/amzonboarding/", {
        params: {},
        headers: { Authorization: AuthHeader },
      })
      .then((res) => {
        const amzOnboardingStatus = res.data;
        localStorage.setItem("AmzOnboard", JSON.stringify(amzOnboardingStatus));
        dispatchUpdateAmzOnboardingStatus(amzOnboardingStatus);
      })
      .catch((err) => {
        dispatchSetErrors(err);
      });
  }

  if (amzadv_code !== null) {
    const params: { [key: string]: number | boolean | string } = {};
    if (searchParams) {
      if (searchParams.get("state")?.includes("additionalAccount")) {
        params["additional_ads_credentials"] = true;
      }
      if (searchParams.get("state")?.includes("reauthorize")) {
        params["reauthorize"] = true;
      }
    }

    axios
      .post(
        URL_PREFIX + "/api/amazonapi/",
        { amazon_authorization_code: amzadv_code },
        { params: params, headers: { Authorization: AuthHeader } },
      )
      .then((res) => {
        if (res.status === 200) {
          refreshStatus();
          code_callback?.();
        }
      })
      .catch((err: AxiosError) => {
        if (err.response?.data === "Failed to fetch tokens from amazon") {
          dispatchSetErrors({
            title: "Advertising Connect Timed Out",
            message: "Connection timeout while verifying your Amazon Advertising credentials. Please try again.",
          });
        } else if (err.response?.data === "No profile IDs found") {
          dispatchSetErrors({
            title: "Invalid Credentials",
            message:
              "Your Amazon credentials are not associated with an Amazon Advertising Account. Please create an Amazon Advertising account to proceed. If you have recently launched your first advertising campaign, please allow up to 1 hour for your profile to be configured.",
          });
        } else if (
          (err.response?.data as string | undefined)?.includes("Amazon Advertising credential mismatch detected")
        ) {
          dispatchSetErrors({
            title: "Credential Mismatch Detected",
            message: err.response?.data,
          });
        }
        // Provided credentials were successfully saved but cannot access the current profile.
        else if (
          (err.response?.data as string | undefined)?.includes(
            "Provided credentials were successfully saved but cannot access the current profile",
          )
        ) {
          dispatchSetErrors({
            title: "Credential Mismatch Detected",
            message:
              "The provided Amazon Advertising credentials were successfully saved but do not have access to your current profile. Please try again with a different account or verify that you are reauthorizing the correct profile.",
          });
        } else {
          dispatchSetErrors({
            title: "Advertising Connect Failed",
            message:
              "Failed to get Amazon Advertising credentials. Please verify the account is set up for advertising.",
          });
        }
        refreshStatus();
      });
  } else {
    refreshStatus();
  }
};
